import { useEffect, useRef, useState } from "react";
import { dayName, dayNum } from "../lib/utils";
import { useDragScroll } from "../lib/useDragScroll";
import { useScrollSync, useScrollSyncControls } from "../lib/scrollSync";
import type { ScoredDay } from "../lib/types";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import styles from "./DaySelector.module.css";

interface DaySelectorProps {
  /** Days to render. `undefined` triggers skeleton mode. */
  days: ScoredDay[] | undefined;
  /** Skeleton placeholder count when `days === undefined`. */
  skeletonCount?: number;

  // ── Selection (detail page) ──────────────────────────────────────────────
  /** Render items as buttons with click-to-select. */
  selectable?: boolean;
  /** dayOffset of the currently selected day. Only used when `selectable`. */
  selectedDay?: number;
  /** Called when a selectable day is clicked. */
  onSelectDay?: (dayOffset: number) => void;
}

/** Today's date + the next N-1 days, as "YYYY-MM-DD" in local time.
 *  Used to fill the strip with real labels before /api/forecast resolves
 *  — dates are deterministic, so there's no reason to show "___ 0"
 *  placeholders while we wait. */
function skeletonDates(count: number): string[] {
  const out: string[] = [];
  const base = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${day}`);
  }
  return out;
}

/**
 * Combined day strip + day picker. The Figma design unifies these into a
 * single component:
 *   - `selectable=false` (overview page): days render as static labels
 *   - `selectable=true` (detail page): days render as buttons with active
 *     outline + click-to-select behaviour
 *
 * On viewports below 600px the strip scrolls horizontally and shows
 * left/right chevron buttons overlaid on the strip edges. A gradient
 * overlay on the right side fades the rightmost item when more content
 * is scrollable.
 */
export function DaySelector({
  days,
  skeletonCount = 7,
  selectable = false,
  selectedDay,
  onSelectDay,
}: DaySelectorProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useDragScroll(scrollRef);
  // Register with the surrounding ScrollSyncProvider if there is one.
  // No-op outside a provider — DetailPage just gets local-only scrolling.
  useScrollSync(scrollRef);
  const controls = useScrollSyncControls();

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // Track scroll position for arrow-disabled state and gradient visibility.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft >= max - 1 || max <= 0);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [days?.length]);

  // When `selectedDay` changes externally (HourlyChart scrubbing on the
  // detail page, or the overview's "Best wind this week" auto-selecting
  // the global peak day), scroll the matching pill into view if it's
  // offscreen. Use the scroll-sync controls when available so each
  // spot card's forecast row tracks the strip in lockstep — otherwise
  // the strip pans but the forecast cells stay parked.
  //
  // `lastScrolledForRef` ensures we only scroll once per unique
  // `selectedDay` value. Without it, any re-render that happens to
  // change `controls`' identity would fire this effect again — and if
  // the user has manually scrolled the strip away from the selected
  // day in the meantime, the strip would snap right back. Recording
  // the day we scrolled to means subsequent renders with the same
  // selectedDay become no-ops, leaving the user in control of the
  // strip after the initial auto-scroll.
  const lastScrolledForRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!selectable || selectedDay === undefined) return;
    if (lastScrolledForRef.current === selectedDay) return;
    const el = scrollRef.current;
    if (!el) return;
    const target = el.querySelector(
      `[data-day-offset="${selectedDay}"]`,
    ) as HTMLElement | null;
    if (!target) return;
    const left = el.scrollLeft;
    const right = left + el.clientWidth;
    const tLeft = target.offsetLeft;
    const tRight = tLeft + target.offsetWidth;
    if (tLeft < left || tRight > right) {
      const offset = Math.max(0, tLeft - 16);
      if (controls) {
        controls.scrollAllTo(offset, el);
      } else {
        el.scrollTo({ left: offset, behavior: "smooth" });
      }
    }
    lastScrolledForRef.current = selectedDay;
  }, [selectable, selectedDay, controls]);

  const handleScrollToStart = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (controls) {
      controls.scrollAllToStart(el);
    } else {
      el.scrollTo({ left: 0, behavior: "smooth" });
    }
  };
  const handleScrollToEnd = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (controls) {
      controls.scrollAllToEnd(el);
    } else {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    }
  };

  return (
    <div className={styles.daySelector}>
      <div className={styles.itemsWrap}>
        <div ref={scrollRef} className={styles.scrollStrip}>
          {!days
            ? skeletonDates(skeletonCount).map((date, i) => (
                <div
                  key={i}
                  className={[styles.dayItem, styles.dayItemSkeleton].join(" ")}
                >
                  <span className={styles.dayName}>{dayName(date)}</span>
                  <span className={styles.dayNum}>{dayNum(date)}</span>
                </div>
              ))
            : days.map((day) => {
                const isActive =
                  selectable && day.dayOffset === selectedDay;
                if (selectable) {
                  return (
                    <button
                      type="button"
                      key={day.dayOffset}
                      className={[styles.dayItem, isActive && styles.dayItemActive]
                        .filter(Boolean)
                        .join(" ")}
                      data-day-offset={day.dayOffset}
                      onClick={() => onSelectDay?.(day.dayOffset)}
                    >
                      <span className={styles.dayName}>{dayName(day.date)}</span>
                      <span className={styles.dayNum}>{dayNum(day.date)}</span>
                    </button>
                  );
                }
                return (
                  <div
                    key={day.dayOffset}
                    className={styles.dayItem}
                    data-day-offset={day.dayOffset}
                  >
                    <span className={styles.dayName}>{dayName(day.date)}</span>
                    <span className={styles.dayNum}>{dayNum(day.date)}</span>
                  </div>
                );
              })}
        </div>

        {/* Right-edge fade — hidden when fully scrolled to the end. */}
        <div
          className={styles.rightFade}
          aria-hidden
          data-hidden={atEnd ? "true" : undefined}
        />

        {/* Inline chevrons overlaid at the left/right edges of the strip.
            Hidden via CSS at the breakpoint where all 7 days fit. */}
        <div className={styles.chevrons}>
          <IconButton
            onClick={handleScrollToStart}
            disabled={atStart}
            aria-label="Show earlier days"
          >
            <Icon name="chevron-left" />
          </IconButton>
          <IconButton
            onClick={handleScrollToEnd}
            disabled={atEnd}
            aria-label="Show later days"
          >
            <Icon name="chevron-right" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
